import React, { useEffect, useMemo, useRef, useState } from "react";
import * as PR from "../../prime-react/index";
import { logo } from "../../assets/images";
import { useSelector, useDispatch } from 'react-redux';
import { authActions } from "../../store/auth";
import { Link, useNavigate } from 'react-router-dom';
import Footer from "../../layouts/Footer";
import { loginOtp, resendOtp } from "../../services/api";
import { capitalizeFirstLetter, resendOtpTime } from "../../utils/reuse";

const OTP = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loginData = useSelector(state => state.auth.loginData);
    const maskedValue = loginData?.passKey?.slice(-4);

    const [errMsg, setErrMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const initialTime = resendOtpTime;
    const [timeLeft, setTimeLeft] = useState(initialTime);
    const [showResend, setShowResend] = useState(false);

    const [otp, setOtp] = useState(new Array(6).fill(''));
    const inputRefs = useRef([]);
    const headers = useMemo(() => {
        return { 'X-Login-Token': loginData.loginToken };
      }, [loginData.loginToken]);

    useEffect(() => {
        // Set an interval to count down every second
        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer); // Clear the interval when time is up
                    setShowResend(true);
                    setOtp(new Array(6).fill(''));
                    setErrMsg('');
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        // Cleanup interval on component unmount
        return () => clearInterval(timer);
    }, [timeLeft]);

    // Format the time left in MM:SS format
    const formatTime = (time) => {
        const minutes = String(Math.floor(time / 60)).padStart(2, '0');
        const seconds = String(time % 60).padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const handleChange = (element, index) => {
        const value = element.value;
        if (/^[0-9]$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            // Move focus to next input
            if (index < 5) {
                inputRefs.current[index + 1].focus();
            }
        } else if (value === '') {
            const newOtp = [...otp];
            newOtp[index] = '';
            setOtp(newOtp);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            if (otp[index] === '') {
                if (index > 0) {
                    inputRefs.current[index - 1].focus();
                }
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(pasteData)) {
            const pasteOtp = pasteData.split('');
            setOtp(pasteOtp);
            // Update all input fields with pasted data
            pasteOtp.forEach((digit, idx) => {
                if (inputRefs.current[idx]) {
                    inputRefs.current[idx].value = digit;
                }
            });
            // Optionally, move focus to the last input or submit the OTP
            inputRefs.current[5].focus();
        }
    };
    
    const isTooManyOtpAttempts = errMsg?.toLowerCase()?.includes("too many otp attempts");

    // Submit OTP
    const handleSubmit = () => {
        if (isTooManyOtpAttempts) {
            navigate('/login');
            return;
        }
        setLoading(true);
        const isOtpComplete = otp.every(digit => digit !== '');
        if (!isOtpComplete) {
            setLoading(false);
            setErrMsg('Please enter the complete OTP');
            return;
        }
        const request = { otp: otp.join('') };
        const getOTPResponse = (response) => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                setErrMsg('');
                setOtp(new Array(6).fill(''));
                dispatch(authActions.onOTPverify(response.data));                
            } else {
                const error = response.data;
                setErrMsg(error === 'otp invalid or expired' ? 'OTP is Invalid / Expired' : capitalizeFirstLetter(error));
            }
        }
        loginOtp(request, headers, getOTPResponse)
    }

    // Resend OTP
    const handleResendOtp = () => {
        setResendLoading(true);
        setOtp(new Array(6).fill(''));
        const getresendOTPResponse = (response) => {
            setResendLoading(false);
            if (response.result === 'SUCCESS') {
                setErrMsg('');
                setTimeLeft(initialTime);
                setShowResend(false);
            } else {
                setErrMsg('The session has expired, please try logging in again');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        }
        resendOtp({}, headers, getresendOTPResponse)
    }

    useEffect(() => {
        if (!loginData?.passKey) {
            navigate('/');
        }
    }, [loginData?.passKey, navigate])

    return (
        <>
            <section className="login-section">
                <div className="grid grid-nogutter">
                    <div className="col-12">
                        <div className="login-wrapper flex align-items-center justify-content-center">
                            <div className="login-info">
                                <PR.Image src={logo} alt="IBIA Logo" className="logo-img" />
                                <h1>Enter OTP</h1>
                                <p>Please enter the One Time Passcode sent to your registered email address.</p>
                                <p className="mt-3">OTP for user reference key ends with ******<b>{maskedValue}</b></p>
                                {/* Form section starts here */}
                                <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                                    <div className='grid'>
                                        <div className="col-12 md:col-12 text-center otp-input-block">
                                            <div className="flex gap-2 justify-content-center align-items-center">
                                            {otp.map((data, index) => (
                                                <PR.InputText
                                                    key={index}
                                                    id={index}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength="1"
                                                    autoComplete="off"
                                                    className="otp-input"
                                                    value={otp[index]}
                                                    onChange={(e) => handleChange(e.target, index)}
                                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                                    onPaste={index === 0 ? handlePaste : null} // Attach onPaste to the first input
                                                    ref={(el) => inputRefs.current[index] = el}
                                                    aria-label={`OTP digit ${index + 1}`}
                                                />
                                            ))}
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-12 text-center otp-input-block">
                                            {!showResend ? (
                                                <p>Resend link will be available in: <b>{formatTime(timeLeft)}</b></p>
                                            )
                                                : <label htmlFor="email" className={`mt-3 block resend-label ${resendLoading ? 'disabled' : ''}`} onClick={resendLoading ? undefined : handleResendOtp}>Resend OTP</label>
                                            }
                                        </div>
                                        <div className="col-12 mt-2">
                                            {isTooManyOtpAttempts ? (
                                                <PR.Button
                                                    label="Back to login"
                                                    type="button"
                                                    className="signin-button w-12"
                                                    onClick={() => navigate('/login')}
                                                />
                                            ) : (
                                                <PR.Button
                                                    loading={loading}
                                                    disabled={loading || resendLoading}
                                                    label={loading ? "Signing in..." : "Sign in"}
                                                    type='submit'
                                                    className="signin-button w-12"
                                                />
                                            )}
                                        </div>
                                        {errMsg && errMsg !== '' &&
                                            <div className="col-12 md:col-12">
                                                <div className='error-message text-center'>{errMsg}</div>
                                            </div>
                                        }
                                        <div className="col-12">
                                            <p className="text-center link-p"><Link to="/forgot-password">Can't access your account?</Link></p>
                                        </div>
                                    </div>
                                </form>
                                {/* Form section ends here */}
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </section>
        </>
    );
};

export default OTP;
